import CitationFlow from '@chegg-wt/core/components/CitationFlow'
import Drawer from '@chegg-wt/core/components/Drawer'
import { ValidatorState } from '@chegg-wt/core/state/citation/types'
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { SESSION_STORAGE_KEYS } from '@chegg-wt/core/utils/sessionStorage'
import { SOURCE_STEP, FORM_STEP } from '@chegg-wt/core/constants/citationFlow'
import { defaultSourceType, sourceTypeByApiAlias } from '@chegg-wt/core/utils/sourceTypes'
import { CitationFlowStep } from '@chegg-wt/core/types/citation-flow'
import { AppRoute, AppRouteOpts, SourceType, Source } from '@chegg-wt/core/types/core'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { addAutociteRioMetadata } from '@chegg-wt/core/state/citation/actions'
import { sendPlusCiteRioEvent } from '@chegg-wt/core/api/plusCiteRioEvent'
import { updateMessagingFlagSelector } from '@chegg-wt/core/state/app/selectors'
import { softLoggedInSelector, jwtSelector } from '@chegg-wt/core/state/user/selectors'
import { AppState } from '@chegg-wt/core/state'
import { getClientCommonFields } from '@chegg-wt/core/utils/plusCiteRioEvent'
import {
  autociteApiVersionSelector,
  autociteV0RioOptFlagSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { AutociteMetadataInput } from '@chegg-wt/core/globalTypesOneGraph'
import { useRoutesContext } from '../RoutesContext'
import { SubHeadingBCRest } from '../BrandConsolidation'
import {
  TESTID_FORM_STEP,
  TESTID_SOURCE_STEP,
  TESTID_SOURCE_STEP_TITLE,
  TESTID_FORM_STEP_TITLE,
} from './constants'
import * as Styled from './styled'

export type CitationFlowDrawerProps = OwnProps & DispatchProps & StateProps

interface OwnProps {
  step: CitationFlowStep
  projectId: string
  citationStyle: string
  initialSourceType?: SourceType
  initialSearchQuery?: string
  editingCitation?: any
  onComplete: (citationId: string, citationStatus: ValidatorState) => void
  onCancel: () => void
}

interface DispatchProps {
  navigated: (appRoute: AppRoute, opts?: AppRouteOpts) => void
  addAutociteRioMetadata: (
    autociteRioTrackingID?: string,
    autociteMetadataInput?: AutociteMetadataInput
  ) => void
}

interface StateProps {
  isSoftLoggedIn: boolean
  cheggUserUuid?: string | null
  autociteV0RioOptFlag?: boolean
  autociteApiVersion: string
  updateMessagingFeatureEnabled: boolean
}

const stepTitle = (step: CitationFlowStep, sourceType: SourceType): string => {
  switch (step) {
    case SOURCE_STEP:
      return 'Find your source'
    case FORM_STEP:
      return `Finalize ${sourceType.name.toLowerCase()} details`
    default:
      return ''
  }
}

const stepTestId = (step: CitationFlowStep): string => {
  switch (step) {
    case SOURCE_STEP:
      return TESTID_SOURCE_STEP
    case FORM_STEP:
      return TESTID_FORM_STEP
    default:
      return ''
  }
}

const stepTitleTestId = (step: CitationFlowStep): string => {
  switch (step) {
    case SOURCE_STEP:
      return TESTID_SOURCE_STEP_TITLE
    case FORM_STEP:
      return TESTID_FORM_STEP_TITLE
    default:
      return ''
  }
}

export const CitationFlowDrawer: React.FunctionComponent<CitationFlowDrawerProps> = ({
  step,
  projectId,
  citationStyle,
  initialSourceType,
  initialSearchQuery,
  navigated,
  addAutociteRioMetadata,
  editingCitation,
  onComplete,
  onCancel,
  cheggUserUuid,
  isSoftLoggedIn,
  autociteV0RioOptFlag,
  autociteApiVersion,
  updateMessagingFeatureEnabled,
}) => {
  const routes = useRoutesContext()

  const [sourceType, setSourceType] = useState<SourceType>(
    initialSourceType || defaultSourceType
  )
  const [citation, setCitation] = useState<any>(null)

  const [isOpen, setIsOpen] = useState(!!step)
  useEffect(() => {
    setIsOpen(!!step)
  }, [step])

  useEffect(() => {
    if (editingCitation) {
      setCitation(editingCitation)
      addAutociteRioMetadata(null)
      setSourceType(sourceTypeByApiAlias(editingCitation.source))
    } else {
      const searchData = JSON.parse(
        sessionStorage.getItem(SESSION_STORAGE_KEYS.searchData)
      )
      const citation = searchData?.data ?? null
      setCitation(citation)
      addAutociteRioMetadata(
        searchData?.autociteRioTrackingID,
        searchData?.autociteResponseForRio
      )
    }
  }, [editingCitation])

  const handleClose = () => {
    setIsOpen(false)
    navigated(routes.toCitationList({ projectId }), { replace: true })
    onCancel()
  }

  const handleSelectSource = (source?: Source) => {
    setCitation(source?.data)
    addAutociteRioMetadata(source?.autociteRioTrackingID, source?.autociteResponseForRio)
    if (
      (autociteApiVersion === 'v0' || autociteV0RioOptFlag) &&
      source?.autociteRioTrackingID
    ) {
      sendPlusCiteRioEvent(
        source?.autociteResponseForRio,
        source?.numFieldsAutocite,
        source?.autociteRioTrackingID,
        getClientCommonFields(isSoftLoggedIn, cheggUserUuid)
      )
    }
    navigated(routes.toCitationListForm({ projectId }), { replace: true })
  }

  const handleBack = () => {
    if (editingCitation) return handleClose()
    if (step === FORM_STEP) {
      if (sourceType.manual) {
        setSourceType(defaultSourceType)
      }
      setCitation(null)
      addAutociteRioMetadata(null)
      navigated(routes.toCitationListSource({ projectId }), { replace: true })
    }
  }

  const handleComplete = (
    citationId: string,
    status: ValidatorState,
    newProjectId?: string
  ) => {
    setCitation(null)
    addAutociteRioMetadata(null)
    onComplete(citationId, status)
    navigated(routes.toCitationList({ projectId: newProjectId || projectId }), {
      replace: true,
    })
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <Styled.CitationFlowContainer data-test={stepTestId(step)}>
        <Styled.CitationDrawerHeaderWrapper>
          <Styled.CitationFlowTitle data-test={stepTitleTestId(step)}>
            {stepTitle(step, sourceType)}
          </Styled.CitationFlowTitle>
          {updateMessagingFeatureEnabled && <SubHeadingBCRest citationFD />}
        </Styled.CitationDrawerHeaderWrapper>
        {step && (
          <CitationFlow
            step={step}
            citationStyle={citationStyle}
            sourceType={sourceType}
            initialSearchQuery={initialSearchQuery}
            citation={citation}
            projectId={projectId}
            isEdit={!!editingCitation}
            onChangeSourceType={setSourceType}
            onSelectSource={handleSelectSource}
            onBack={handleBack}
            onComplete={handleComplete}
          />
        )}
      </Styled.CitationFlowContainer>
    </Drawer>
  )
}

const mapStateToProps = (state: AppState): StateProps => ({
  isSoftLoggedIn: softLoggedInSelector(state),
  autociteV0RioOptFlag: autociteV0RioOptFlagSelector(state),
  autociteApiVersion: autociteApiVersionSelector(state),
  cheggUserUuid: jwtSelector(state)?.cheggUserUuid,
  updateMessagingFeatureEnabled: updateMessagingFlagSelector(state),
})

const mapDispatchToProps: DispatchProps = {
  navigated,
  addAutociteRioMetadata,
}

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(CitationFlowDrawer)
